import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import namespace translations
import { commonTranslations } from './namespaces/common';
import { kciTranslations } from './namespaces/kci';
import { userSelectorTranslations } from './namespaces/user-selector';
import { goalsTranslations } from './namespaces/goals';
import { tasksTranslations } from './namespaces/tasks';
import { onboardingTranslations } from './namespaces/onboarding';
import { interactionsTranslations } from './namespaces/interactions';
import { openBlockageTranslations } from './namespaces/open-blockage';
import { oneOnOneTranslations } from './namespaces/one-on-one';
import { privacyPolicyTranslations } from './namespaces/privacy-policy';
import { termsOfUseTranslations } from './namespaces/terms-of-use';
import { knowledgeTranslations } from './namespaces/knowledge';
import { debriefV2Translations } from './namespaces/debriefV2';
import { wizardTranslations } from '@momentum/i18n';

type TranslationNamespace = {
  en: Record<string, unknown>;
  he: Record<string, unknown>;
};

const translationNamespaces = {
  common: commonTranslations,
  kci: kciTranslations,
  userSelector: userSelectorTranslations,
  goals: goalsTranslations,
  tasks: tasksTranslations,
  onboarding: onboardingTranslations,
  interactions: interactionsTranslations,
  openBlockage: openBlockageTranslations,
  oneOnOne: oneOnOneTranslations,
  privacyPolicy: privacyPolicyTranslations,
  termsOfUse: termsOfUseTranslations,
  knowledge: knowledgeTranslations,
  debriefV2: debriefV2Translations,
  wizard: wizardTranslations,
} satisfies Record<string, TranslationNamespace>;

const buildResources = <T extends Record<string, TranslationNamespace>>(
  namespaces: T
) => {
  return {
    en: Object.fromEntries(
      Object.entries(namespaces).map(([key, value]) => [key, value.en])
    ),
    he: Object.fromEntries(
      Object.entries(namespaces).map(([key, value]) => [key, value.he])
    ),
  } as {
    en: { [K in keyof T]: T[K]['en'] };
    he: { [K in keyof T]: T[K]['he'] };
  };
};

const resources = buildResources(translationNamespaces);

const namespaceNames = Object.keys(
  translationNamespaces
) as (keyof typeof translationNamespaces)[];

// Export for type declaration
export const defaultNS = 'common';
export const fallbackNS = 'common';
export { resources };

// Date formatting configuration
export const dateFormatConfig = {
  en: {
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    shortMonths: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    days: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    weekStartsOn: 0,
  },
  he: {
    months: [
      'ינואר',
      'פברואר',
      'מרץ',
      'אפריל',
      'מאי',
      'יוני',
      'יולי',
      'אוגוסט',
      'ספטמבר',
      'אוקטובר',
      'נובמבר',
      'דצמבר',
    ],
    shortMonths: [
      'ינו',
      'פבר',
      'מרץ',
      'אפר',
      'מאי',
      'יונ',
      'יול',
      'אוג',
      'ספט',
      'אוק',
      'נוב',
      'דצמ',
    ],
    days: ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'],
    shortDays: ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'],
    weekStartsOn: 0,
  },
};

// Export initialization function instead of auto-initializing
export const initializeI18n = () => {
  return i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      ns: namespaceNames, // Automatically includes all registered namespaces
      defaultNS, // Default namespace for keys without prefix
      fallbackNS, // Fallback when key not found in current namespace
      supportedLngs: ['en', 'he'],
      fallbackLng: 'en',
      debug: process.env.NODE_ENV === 'development',
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        caches: ['localStorage'],
      },
    });
};

export default i18n;
